import { useReducer } from 'react'
import {
  compose,
  filter,
  map,
  sortWith,
  prop,
  ascend,
  includes,
  find,
  clone,
} from 'ramda'
import {
  PLACE_INVITATION_ACCEPT_REQUEST,
  PLACE_INVITATION_ACCEPT_INVITATION,
  PLACE_INVITATION_PUBLIC_SUBSCRIBE,
} from 'app/configs/statusConfigs'
import {
  CONTACT_TYPE_APP,
  CONTACT_TYPE_CRM,
} from 'app/main/notification/socialMediaSelect/socialMediaSelectConfig'
import { useIntl } from 'react-intl'
import { useAppName } from 'commons/usePartner'
import { CHANNEL_SMS, CHANNEL_EMAIL, CHANNEL_CALL } from 'app/constants/channel'
import { isEmptyOrNil } from 'libs/empty'

export const ActionType = {
  SET_PREFER_CHANNEL: 'SET_PREFER_CHANNEL',
}

const initialState = {
  preferOptions: [],
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_PREFER_CHANNEL:
      return {
        ...state,
        preferOptions: action.payload,
      }

    default:
      return state
  }
}

export default function usePreferredChannel(subscriber) {
  const { formatMessage } = useIntl()
  const [state, dispatch] = useReducer(reducer, initialState)
  const appName = useAppName()
  const appLabel = formatMessage(
    {
      id: 'crm.form.notificationMethod.app',
    },
    {
      appName,
    },
  )

  const getPreferOptionsFromSubscriber = () => {
    const subscriberClone = clone(subscriber)
    const isSubscribed = includes(subscriber.lastInvitationStatus, [
      PLACE_INVITATION_ACCEPT_REQUEST,
      PLACE_INVITATION_ACCEPT_INVITATION,
      PLACE_INVITATION_PUBLIC_SUBSCRIBE,
    ])
    let prefer = compose(
      filter((item) => {
        return isSubscribed ? item.isVerified === 1 : true
      }),
      map((item) => {
        if (item.contactType === CONTACT_TYPE_APP) {
          item.contact = appLabel
        }
        return item
      }),
      sortWith([ascend(prop('contactType')), ascend(prop('channel'))]),
      prop('receiveMethods'),
    )(subscriberClone)

    if (!isEmptyOrNil(subscriberClone.contact.email)) {
      const preferEmail = find((item) => {
        return (
          item.channel === CHANNEL_EMAIL &&
          item.contactType === CONTACT_TYPE_CRM
        )
      }, prefer)

      if (isEmptyOrNil(preferEmail)) {
        prefer = [
          ...prefer,
          {
            id: null,
            channel: CHANNEL_EMAIL,
            contact: subscriberClone.contact.email,
            contactType: CONTACT_TYPE_CRM,
            isSelected: 0,
            isVerified: 1,
            isBadContact: false,
          },
        ]
      }
    }
    if (!isEmptyOrNil(subscriberClone.contact.phoneNumber)) {
      const preferSms = find((item) => {
        return (
          item.channel === CHANNEL_SMS && item.contactType === CONTACT_TYPE_CRM
        )
      }, prefer)

      if (isEmptyOrNil(preferSms)) {
        prefer = [
          ...prefer,
          {
            id: null,
            channel: CHANNEL_SMS,
            contact: `${subscriberClone.contact.countryCode}${subscriberClone.contact.phoneNumber}`,
            contactType: CONTACT_TYPE_CRM,
            isSelected: 0,
            isVerified: 1,
            isBadContact: false,
          },
        ]
      }
    }

    if (!isEmptyOrNil(subscriberClone.contact.automatedCallPhoneNumber)) {
      const preferCall = find((item) => {
        return (
          item.channel === CHANNEL_CALL && item.contactType === CONTACT_TYPE_CRM
        )
      }, prefer)

      if (isEmptyOrNil(preferCall)) {
        prefer = [
          ...prefer,
          {
            id: null,
            channel: CHANNEL_CALL,
            contact: `${subscriberClone.contact.automatedCallCountryCode}${subscriberClone.contact.automatedCallPhoneNumber}`,
            contactType: CONTACT_TYPE_CRM,
            isSelected: 0,
            isVerified: 1,
            isBadContact: false,
          },
        ]
      }
    }

    dispatch({ type: ActionType.SET_PREFER_CHANNEL, payload: prefer })
  }

  const addOption = ({ channel, contact }) => {
    const prefer = [
      ...state.preferOptions,
      {
        id: null,
        channel: channel,
        contact: contact,
        contactType: CONTACT_TYPE_CRM,
        isSelected: 0,
        isVerified: 1,
        isBadContact: false,
      },
    ]
    dispatch({
      type: ActionType.SET_PREFER_CHANNEL,
      payload: prefer,
    })
  }
  const updateOption = ({ channel, contact }) => {
    const prefer = state.preferOptions.map((item) => {
      if (item.channel === channel && item.contactType === CONTACT_TYPE_CRM) {
        item.contact = contact
        item.isBadContact = false
      }
      return item
    })
    dispatch({ type: ActionType.SET_PREFER_CHANNEL, payload: prefer })
  }

  const removeOption = ({ channel }) => {
    const prefer = state.preferOptions.filter((item) => {
      return item.contactType === CONTACT_TYPE_CRM
        ? item.channel !== channel
        : true
    })
    dispatch({ type: ActionType.SET_PREFER_CHANNEL, payload: prefer })
  }

  return {
    state,
    getPreferOptionsFromSubscriber,
    addOption,
    updateOption,
    removeOption,
  }
}
